import { Controller } from '@hotwired/stimulus'
import Sfetch from '@app/utils/sfetch'

interface WithoutDataResponse {
  account_ids: number[]
}

// A scroll crosses rows a few at a time, and each request costs the same whether
// it carries one account or a hundred, so ids wait this long for the ones behind
// them rather than leaving one at a time.
const COLLECT_MS = 50

// Rows render as if every account holds data, and the ones near the viewport are
// asked about in chunks. The cost of a request follows the viewport, so a file
// with thousands of accounts cannot push a single request past the timeout.
export default class extends Controller {
  static values = {
    url: String,
    chunkSize: { type: Number, default: 100 },
  }

  static targets = ['row']

  declare urlValue: string
  declare chunkSizeValue: number
  declare rowTargets: HTMLElement[]

  private observer?: IntersectionObserver
  private rowsById = new Map<string, HTMLElement>()
  private answered = new Set<string>()
  private inFlight = new Set<string>()
  private waiting = new Set<string>()
  private collecting?: ReturnType<typeof setTimeout>

  connect(): void {
    this.rowsById = new Map(
      this.rowTargets.flatMap((row) => {
        const id = row.dataset.accountId
        return id ? [[id, row] as [string, HTMLElement]] : []
      })
    )

    this.observer = new IntersectionObserver(
      (entries) => this.onVisible(entries),
      { rootMargin: '600px' }
    )
    this.rowTargets.forEach((row) => this.observer?.observe(row))
  }

  disconnect(): void {
    if (this.collecting) clearTimeout(this.collecting)
    this.collecting = undefined
    this.observer?.disconnect()
    this.observer = undefined
    this.rowsById.clear()
    this.answered.clear()
    this.inFlight.clear()
    this.waiting.clear()
  }

  private onVisible(entries: IntersectionObserverEntry[]): void {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return

      const id = (entry.target as HTMLElement).dataset.accountId
      if (!id || this.answered.has(id) || this.inFlight.has(id)) return

      this.waiting.add(id)
    })

    if (this.waiting.size === 0 || this.collecting) return

    this.collecting = setTimeout(() => this.send(), COLLECT_MS)
  }

  private send(): void {
    this.collecting = undefined

    const ids = [...this.waiting]
    this.waiting.clear()
    ids.forEach((id) => this.inFlight.add(id))

    for (let from = 0; from < ids.length; from += this.chunkSizeValue) {
      void this.ask(ids.slice(from, from + this.chunkSizeValue))
    }
  }

  private async ask(ids: string[]): Promise<void> {
    try {
      const query = new URLSearchParams()
      ids.forEach((id) => query.append('account_ids[]', id))

      const data = await Sfetch.json<WithoutDataResponse>(
        `${this.urlValue}?${query.toString()}`,
        {},
        { camelizeJSON: false }
      )

      ids.forEach((id) => this.answered.add(id))
      data?.account_ids?.forEach((id) => this.dim(String(id)))
    } catch {
      // The rows stay as they are, which reads as "this account holds data".
      // Scrolling back over them asks again, and AccountsController#destroy
      // re-checks the account before it deletes anything.
    } finally {
      ids.forEach((id) => this.inFlight.delete(id))
    }
  }

  private dim(id: string): void {
    const row = this.rowsById.get(id)
    if (!row) return

    row
      .querySelectorAll('[data-dimmable]')
      .forEach((cell) => cell.classList.add('sf-dim-el'))

    row.querySelector('[data-filler-cell]')?.setAttribute('hidden', '')
    row.querySelector('[data-destroy-cell]')?.removeAttribute('hidden')
  }
}
